import { AnalyticsProvider } from "@components/Analytics/AnalyticsProvider";
import { PullToRefreshProvider } from "@components/PullToRefresh/PullToRefreshProvider";
import { RefreshNotification } from "@components/RefreshNotification";
import { WebSocketProvider } from "@components/WebSocketProvider";
import { URL_OVERRIDABLE_FEATURE_FLAGS } from "@constants/feature-flags";
import { useNotificationVisibility } from "@hooks/useNotificationVisibility";
import { useVersionCheck } from "@hooks/useVersionCheck";
import {
  FeatureFlagProvider,
  PopoverProvider,
  ThemeProvider,
  ThemeWrapper,
  ToastProvider,
} from "@lyteflo/components";
import { Theme } from "@lyteflo/utils";
import { DealerContextBootstrap } from "@providers/DealerContextBootstrap";
import { TooltipProvider } from "@radix-ui/react-tooltip";
import { handlePwaInstallDetection } from "@utils/environment";
import type { ReactNode } from "preact/compat";
import { useEffect } from "preact/hooks";

interface AppProvidersProps {
  children: ReactNode;
}

export const AppProviders = ({ children }: AppProvidersProps) => {
  const { showRefreshNotification, forceRefresh } = useVersionCheck();
  const { isVisible: isNotificationVisible } = useNotificationVisibility();

  useEffect(() => {
    handlePwaInstallDetection();
  }, []);

  return (
    <AnalyticsProvider>
      <ThemeProvider appearance={Theme.LIGHT}>
        <ThemeWrapper>
          <TooltipProvider>
            <ToastProvider initialInset={{ top: 16, right: 4 }}>
              <FeatureFlagProvider
                environmentId={import.meta.env.VITE_FLAGSMITH_ENVIRONMENT_ID}
                forceRefresh
                reidentifyOnTraitChange
                dealerScopedIdentity
                validFlagKeys={URL_OVERRIDABLE_FEATURE_FLAGS}
              >
                <WebSocketProvider>
                  <PopoverProvider>
                    <PullToRefreshProvider />
                    <DealerContextBootstrap>{children}</DealerContextBootstrap>
                    {isNotificationVisible && (
                      <RefreshNotification
                        isVisible={showRefreshNotification}
                        onRefresh={forceRefresh}
                      />
                    )}
                  </PopoverProvider>
                </WebSocketProvider>
              </FeatureFlagProvider>
            </ToastProvider>
          </TooltipProvider>
        </ThemeWrapper>
      </ThemeProvider>
    </AnalyticsProvider>
  );
};
